import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  PackageOpen, Send, X, Trash2, ChevronDown, ChevronRight,
  Truck, MapPin, ClipboardList, Bell, FileText, IndianRupee, RefreshCw,
} from "lucide-react";
import CourierSelect from "@/components/CourierSelect";

// Rate Matrix Engine service types (Feb 2026)
const SERVICE_TYPES = ["Surface", "Air-DOC", "Air-NON-DOC", "Express", "International"];

/**
 * BookParcelDialog — ERP-style responsive modal with fixed header/footer & scrollable body.
 * Business logic identical: POST /api/bookings, respects book_pickup / notify_courier flags.
 * Feb 2026 · additionally queries Rate Matrix Engine (`/api/rate-matrix/quote`) for each parcel
 * whenever Courier + Service Type + Source PIN + Destination PIN + Weight are present. The
 * resolved rate is displayed inline and persisted with the booking. Booking flow itself is
 * unchanged.
 */
export default function BookParcelDialog({ open, onClose, selectedParcels, onBooked }) {
  const [form, setForm] = useState({
    courier: "",
    service_type: "Surface",
    pickup_date: new Date().toISOString().slice(0, 10),
    pickup_time: "14:00",
    pickup_address: "",
    pickup_contact_name: "",
    pickup_contact_phone: "",
    eta_hours: 24,
    remarks: "",
    book_pickup: true,
    notify_courier: true,
    courier_email: "",
  });
  const [busy, setBusy] = useState(false);
  const [removed, setRemoved] = useState({});
  const [openSections, setOpenSections] = useState({ courier: true, pickup: true, booking: true, actions: true, rate: true });
  const [bookingNumber, setBookingNumber] = useState(null);
  const [quotes, setQuotes] = useState({});         // { parcel_id: {price, zone, slab_label, source, error?} }
  const [quotingBusy, setQuotingBusy] = useState(false);

  const parcels = useMemo(
    () => (selectedParcels || []).filter((p) => !removed[p.id]),
    [selectedParcels, removed]
  );
  const totalWeight = useMemo(
    () => parcels.reduce((n, p) => n + (Number(p.weight_kg) || 0), 0),
    [parcels]
  );
  const totalCharges = useMemo(
    () => Object.values(quotes).reduce((n, q) => n + (q?.price ? Number(q.price) : 0), 0),
    [quotes]
  );

  useEffect(() => {
    if (open) { setRemoved({}); setBookingNumber(null); setQuotes({}); }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k) => setOpenSections((s) => ({ ...s, [k]: !s[k] }));

  // Rate Matrix quote — fetch a rate + breakdown for each parcel.
  const fetchQuotes = useCallback(async () => {
    if (!form.courier || !form.service_type || parcels.length === 0) return;
    setQuotingBusy(true);
    const next = {};
    await Promise.all(parcels.map(async (p) => {
      const src = String(p.source_pincode || "").trim();
      const dst = String(p.destination_pincode || (p.recipient_address || "").slice(-6)).trim();
      const wg = Math.max(1, Math.round((Number(p.weight_kg) || 0.5) * 1000));
      if (!src || !dst || dst.length < 3) {
        next[p.id] = { error: "Missing PIN codes" }; return;
      }
      try {
        const { data } = await api.post("/rate-matrix/quote", {
          courier: form.courier, service_type: form.service_type,
          source_pincode: src, destination_pincode: dst, weight_g: wg,
        });
        next[p.id] = data;
      } catch (e) {
        next[p.id] = { error: e?.response?.data?.detail || "Quote failed" };
      }
    }));
    setQuotes(next);
    setQuotingBusy(false);
  }, [form.courier, form.service_type, parcels]);

  // Service Recommendation — quote every configured service for the FIRST parcel and let the
  // operator switch to the cheapest with one click. Refreshes when courier/weight/route changes.
  const [recommendation, setRecommendation] = useState(null);
  const [recBusy, setRecBusy] = useState(false);
  useEffect(() => {
    if (!form.courier || parcels.length === 0) { setRecommendation(null); return; }
    const p = parcels[0];
    const src = String(p.source_pincode || "").trim();
    const dst = String(p.destination_pincode || "").trim();
    const wg = Math.max(1, Math.round((Number(p.weight_kg) || 0.5) * 1000));
    if (!src || !dst || dst.length < 3) { setRecommendation(null); return; }
    setRecBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/rate-matrix/recommend", {
          courier: form.courier, service_type: form.service_type,
          source_pincode: src, destination_pincode: dst, weight_g: wg,
        });
        setRecommendation(data);
      } catch (_) { setRecommendation(null); }
      finally { setRecBusy(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.courier, form.service_type, parcels]);

  // Auto-refetch quotes whenever courier / service / parcel set changes.
  useEffect(() => {
    const t = setTimeout(fetchQuotes, 350);
    return () => clearTimeout(t);
  }, [fetchQuotes]);

  const expectedDelivery = useMemo(() => {
    try {
      const dt = new Date(`${form.pickup_date}T${form.pickup_time}:00Z`);
      dt.setHours(dt.getHours() + (Number(form.eta_hours) || 0));
      return dt.toISOString().slice(0, 16).replace("T", " ") + " UTC";
    } catch (_e) { return "—"; }
  }, [form.pickup_date, form.pickup_time, form.eta_hours]);

  const validate = () => {
    if (parcels.length === 0) { toast.error("Select at least one parcel"); return false; }
    if (!form.courier) { toast.error("Courier is required"); return false; }
    if (!form.pickup_address.trim() || !form.pickup_contact_name.trim() ||
        !form.pickup_contact_phone.trim()) {
      toast.error("Pickup address & contact details are required"); return false;
    }
    return true;
  };

  const submitWith = async ({ book_pickup, notify_courier, draft = false }) => {
    if (draft) {
      try {
        localStorage.setItem(
          "mr_booking_draft",
          JSON.stringify({ form, parcel_ids: parcels.map((p) => p.id), saved_at: new Date().toISOString() })
        );
        toast.success("Draft saved locally", { description: "Reopen this dialog to resume." });
      } catch (_e) { toast.error("Could not save draft"); }
      return;
    }
    if (!validate()) return;
    setBusy(true);
    try {
      // Build rate_details array from cached quotes
      const rate_details = parcels.map((p) => {
        const q = quotes[p.id] || {};
        return {
          parcel_id: p.id,
          tracking_number: p.tracking_number,
          zone: q.zone || null,
          slab_label: q.slab_label || null,
          price: (q.price != null && !q.error) ? Number(q.price) : null,
          source: q.source || null,
          error: q.error || null,
        };
      });
      const payload = {
        ...form,
        parcel_ids: parcels.map((p) => p.id),
        book_pickup: !!book_pickup,
        notify_courier: !!notify_courier,
        total_charges: totalCharges > 0 ? Number(totalCharges.toFixed(2)) : null,
        rate_details,
      };
      const { data } = await api.post("/bookings", payload);
      setBookingNumber(data.booking_number);
      toast.success(`Booking ${data.booking_number} · ${parcels.length} parcels${totalCharges > 0 ? ` · ₹${totalCharges.toFixed(2)}` : ""}`, {
        description: data.manifest_generated
          ? "Manifest PDF generated and emailed."
          : (data.email_sent ? "Courier notified via email." : "SLA started."),
      });
      onBooked?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="p-0 gap-0 rounded-xl w-[calc(100vw-2rem)] max-w-[1100px] max-h-[90vh] flex flex-col overflow-hidden"
        data-testid="book-parcel-dialog"
      >
        <DialogTitle className="sr-only">Book Parcel</DialogTitle>
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border bg-card shrink-0">
          <PackageOpen size={18} className="text-primary shrink-0" />
          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
            <span className="font-display text-xl truncate">Book Parcel</span>
            <span className="text-[11px] uppercase tracking-widest font-mono bg-primary text-primary-foreground px-2 py-0.5 rounded"
              data-testid="book-selected-count">{parcels.length} selected</span>
            {bookingNumber && (
              <span className="text-[11px] uppercase tracking-widest font-mono bg-emerald-600 text-white px-2 py-0.5 rounded"
                data-testid="book-booking-number">{bookingNumber}</span>
            )}
            <span className="text-[11px] mono text-muted-foreground ml-1 hidden sm:inline">
              Total {totalWeight.toFixed(2)} kg{totalCharges > 0 ? ` · ₹${totalCharges.toFixed(2)}` : ""}
            </span>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-muted"
                  data-testid="book-close" aria-label="Close"><X size={16} /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3" data-testid="book-body">
          <SectionCard icon={FileText} title="Selected parcels"
                        hint={`${parcels.length} · ${totalWeight.toFixed(2)} kg`}
                        open={openSections.parcels ?? true} onToggle={() => toggle("parcels")}
                        testid="section-parcels">
            <div className="border border-border rounded-md max-h-32 overflow-y-auto"
                 data-testid="book-parcels-list">
              {parcels.length === 0 && (
                <div className="p-3 text-xs text-muted-foreground text-center">
                  All parcels removed. Close & re-select.
                </div>
              )}
              {parcels.map((p) => (
                <div key={p.id}
                     className="flex items-center justify-between px-3 py-1.5 border-b border-border last:border-b-0 text-xs">
                  <div className="font-mono truncate">
                    <span className="font-bold">{p.tracking_number}</span>
                    <span className="text-muted-foreground ml-2">
                      {p.sender_name} → {p.recipient_name}
                    </span>
                    <span className="text-[10px] text-muted-foreground ml-2">
                      {p.source_pincode || "??"} → {p.destination_pincode || "??"} · {(Number(p.weight_kg) || 0).toFixed(2)}kg
                    </span>
                  </div>
                  <button type="button"
                          onClick={() => setRemoved({ ...removed, [p.id]: true })}
                          className="text-muted-foreground hover:text-destructive shrink-0"
                          data-testid={`book-remove-${p.tracking_number}`}
                          title="Remove from booking"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard icon={Truck} title="Courier information"
                        open={openSections.courier} onToggle={() => toggle("courier")}
                        testid="section-courier">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="block">
                <span className="overline">Courier *</span>
                <CourierSelect value={form.courier} onChange={(v) => set("courier", v)}
                                testid="book-courier" />
              </label>
              <label className="block">
                <span className="overline">Service Type *</span>
                <select data-testid="book-service-type" value={form.service_type}
                        onChange={(e) => set("service_type", e.target.value)}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="overline">ETA (hours) *</span>
                <Input data-testid="book-eta" type="number" step="0.5" min="0"
                       value={form.eta_hours}
                       onChange={(e) => set("eta_hours", parseFloat(e.target.value) || 0)}
                       className="mt-1 rounded-md font-mono" required />
              </label>
            </div>
          </SectionCard>

          {/* Rate Matrix Engine · live quote preview + service recommendation */}
          <SectionCard icon={IndianRupee} title="Rate Matrix"
                        hint={totalCharges > 0 ? `Total ₹${totalCharges.toFixed(2)}` : (quotingBusy ? "Quoting…" : "—")}
                        open={openSections.rate} onToggle={() => toggle("rate")}
                        testid="section-rate">
            {/* Service Recommendation panel */}
            {recommendation && (recommendation.results?.length > 0) && (
              <div className="border border-amber-300 bg-amber-50/60 rounded-md p-3 mb-3"
                   data-testid="book-rec-panel">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[9px] uppercase tracking-widest bg-amber-500 text-white px-1.5 py-0.5 rounded">Recommendation</span>
                  <span className="text-[11px] text-muted-foreground">
                    Cheapest available for {form.courier} · {parcels[0]?.source_pincode || "?"} → {parcels[0]?.destination_pincode || "?"} · {(Number(parcels[0]?.weight_kg) || 0).toFixed(2)}kg
                  </span>
                  {recBusy && <span className="text-[10px] text-muted-foreground">…refreshing</span>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {recommendation.results.map((r) => {
                    const isRec = recommendation.recommended?.service_type === r.service_type;
                    const isPicked = form.service_type === r.service_type;
                    const eligible = r.total != null;
                    return (
                      <button key={r.service_type} type="button"
                              disabled={!eligible}
                              onClick={() => eligible && set("service_type", r.service_type)}
                              data-testid={`book-rec-opt-${r.service_type}`}
                              className={`text-left rounded-md border p-2 transition ${
                                !eligible ? "bg-muted/40 text-muted-foreground opacity-60 cursor-not-allowed" :
                                isPicked ? "border-primary bg-primary/10" :
                                isRec ? "border-amber-500 bg-white hover:-translate-y-0.5" :
                                "border-border bg-card hover:-translate-y-0.5"}`}>
                        <div className="flex items-center gap-1 text-[11px]">
                          {isRec && <span className="text-amber-500">⭐</span>}
                          <b>{r.service_type}</b>
                          {isPicked && <span className="ml-auto text-[9px] text-primary">SELECTED</span>}
                        </div>
                        {eligible ? (
                          <>
                            <div className="mono text-lg font-bold mt-1">₹{Number(r.total).toFixed(2)}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {r.zone} · {r.slab_label} · ETA {r.transit_days || "—"}d
                            </div>
                          </>
                        ) : (
                          <div className="text-[10px] text-red-600 mt-1">{r.reason || "Not eligible"}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] text-muted-foreground">
                Auto-resolves zone → weight slab → price for every parcel using {form.courier || "the selected courier"} · {form.service_type}.
              </div>
              <Button variant="outline" size="sm" onClick={fetchQuotes} disabled={quotingBusy}
                      data-testid="book-rate-refresh" className="h-8 rounded-md">
                <RefreshCw size={11} className={`mr-1 ${quotingBusy ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
            <div className="border border-border rounded-md overflow-hidden" data-testid="book-rate-table">
              <table className="w-full text-[11px] mono">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-1.5">Tracking</th>
                    <th className="text-left p-1.5">Route</th>
                    <th className="text-left p-1.5">Zone · Slab</th>
                    <th className="text-right p-1.5">Freight</th>
                    <th className="text-right p-1.5">Fuel</th>
                    <th className="text-right p-1.5">Handling</th>
                    <th className="text-right p-1.5">GST</th>
                    <th className="text-right p-1.5">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {parcels.map((p) => {
                    const q = quotes[p.id] || {};
                    return (
                      <tr key={p.id} className="border-t border-border"
                          data-testid={`book-rate-row-${p.tracking_number}`}>
                        <td className="p-1.5">{p.tracking_number}</td>
                        <td className="p-1.5">{p.source_pincode || "?"} → {p.destination_pincode || "?"}</td>
                        <td className="p-1.5">{q.zone ? `${q.zone} · ${q.slab_label}` : (q.error ? "—" : "…")}</td>
                        <td className="p-1.5 text-right">{q.base_freight != null ? `₹${Number(q.base_freight).toFixed(2)}` : (q.error ? "—" : "…")}</td>
                        <td className="p-1.5 text-right">{q.fuel_surcharge != null ? `₹${Number(q.fuel_surcharge).toFixed(2)}` : "—"}</td>
                        <td className="p-1.5 text-right">{q.handling_charge != null ? `₹${Number(q.handling_charge).toFixed(2)}` : "—"}</td>
                        <td className="p-1.5 text-right">{q.gst != null ? `₹${Number(q.gst).toFixed(2)}` : "—"}</td>
                        <td className="p-1.5 text-right font-bold">
                          {q.error ? <span className="text-red-600 text-[10px]" title={q.error}>{q.error.length > 40 ? q.error.slice(0, 37) + "…" : q.error}</span>
                           : q.total != null ? `₹${Number(q.total).toFixed(2)}`
                           : "…"}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="border-t border-border bg-muted/40 font-bold">
                    <td className="p-1.5" colSpan={7}>Total charges (all parcels)</td>
                    <td className="p-1.5 text-right" data-testid="book-rate-total">
                      ₹{totalCharges.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              Powered by Dynamic Rate Matrix. Prices refresh whenever Courier / Service / Weight / PIN changes.
            </div>
          </SectionCard>

          <SectionCard icon={MapPin} title="Pickup details" open={openSections.pickup}
                        onToggle={() => toggle("pickup")} testid="section-pickup">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <label className="block">
                <span className="overline">Pickup Date *</span>
                <Input data-testid="book-pickup-date" type="date" value={form.pickup_date}
                       onChange={(e) => set("pickup_date", e.target.value)} className="mt-1 rounded-md" required />
              </label>
              <label className="block">
                <span className="overline">Pickup Time *</span>
                <Input data-testid="book-pickup-time" type="time" value={form.pickup_time}
                       onChange={(e) => set("pickup_time", e.target.value)} className="mt-1 rounded-md" required />
              </label>
              <label className="block">
                <span className="overline">Contact Person *</span>
                <Input data-testid="book-contact-name" value={form.pickup_contact_name}
                       onChange={(e) => set("pickup_contact_name", e.target.value)} className="mt-1 rounded-md" required />
              </label>
              <label className="block">
                <span className="overline">Contact Number *</span>
                <Input data-testid="book-contact-phone" value={form.pickup_contact_phone}
                       onChange={(e) => set("pickup_contact_phone", e.target.value)}
                       className="mt-1 rounded-md font-mono" required />
              </label>
              <label className="block md:col-span-2 lg:col-span-4">
                <span className="overline">Pickup Address *</span>
                <Textarea data-testid="book-pickup-address" value={form.pickup_address}
                          onChange={(e) => set("pickup_address", e.target.value)}
                          className="mt-1 rounded-md resize-y max-h-24" rows={2} required />
              </label>
            </div>
          </SectionCard>

          <SectionCard icon={ClipboardList} title="Booking information" open={openSections.booking}
                        onToggle={() => toggle("booking")} testid="section-booking">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="block">
                <span className="overline">Expected Delivery</span>
                <div className="mt-1 h-10 px-3 rounded-md border border-input bg-muted/40 text-sm flex items-center font-mono"
                     data-testid="book-expected-delivery">{expectedDelivery}</div>
              </div>
              <label className="block">
                <span className="overline">Remarks</span>
                <Textarea data-testid="book-remarks" value={form.remarks}
                          onChange={(e) => set("remarks", e.target.value)}
                          className="mt-1 rounded-md resize-y max-h-24" rows={2}
                          placeholder="Any special instruction for the courier…" />
              </label>
            </div>
          </SectionCard>

          <SectionCard icon={Bell} title="Actions" open={openSections.actions}
                        onToggle={() => toggle("actions")} testid="section-actions">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex items-start gap-2 cursor-pointer p-3 border border-border rounded-md bg-muted/30">
                <input data-testid="book-opt-pickup" type="checkbox" checked={form.book_pickup}
                        onChange={(e) => set("book_pickup", e.target.checked)}
                        className="mt-0.5 accent-[#BE1E1E]" />
                <div>
                  <div className="text-sm font-semibold">Book Pickup</div>
                  <div className="text-[11px] text-muted-foreground">Creates a Pickup Request record (reuses the Pickup module — no duplicates).</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer p-3 border border-border rounded-md bg-muted/30">
                <input data-testid="book-opt-notify" type="checkbox" checked={form.notify_courier}
                        onChange={(e) => set("notify_courier", e.target.checked)}
                        className="mt-0.5 accent-[#BE1E1E]" />
                <div>
                  <div className="text-sm font-semibold">Notify Courier</div>
                  <div className="text-[11px] text-muted-foreground">Emails the courier via SMTP relay. Attaches Pickup Manifest if &gt; 1 parcel.</div>
                </div>
              </label>
              {form.notify_courier && (
                <label className="block md:col-span-2">
                  <span className="overline">Courier email (override)</span>
                  <Input data-testid="book-courier-email" type="email" value={form.courier_email}
                         onChange={(e) => set("courier_email", e.target.value)}
                         placeholder="Leave blank to use Courier Master email"
                         className="mt-1 rounded-md" />
                </label>
              )}
            </div>
          </SectionCard>
        </div>

        <div className="shrink-0 border-t border-border bg-card px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2 justify-end"
             data-testid="book-footer">
          <Button type="button" variant="ghost" onClick={onClose} className="rounded-md"
                  data-testid="book-cancel" disabled={busy}>
            <X size={13} className="mr-1" /> Cancel
          </Button>
          <Button type="button" variant="outline"
                  onClick={() => submitWith({ book_pickup: false, notify_courier: false, draft: true })}
                  className="rounded-md" data-testid="book-save-draft"
                  disabled={busy || parcels.length === 0}>Save Draft</Button>
          <Button type="button" variant="outline"
                  onClick={() => submitWith({ book_pickup: true, notify_courier: false })}
                  className="rounded-md" data-testid="book-only-pickup"
                  disabled={busy || parcels.length === 0}>
            <Truck size={13} className="mr-1" /> Book Pickup
          </Button>
          <Button type="button" variant="outline"
                  onClick={() => submitWith({ book_pickup: false, notify_courier: true })}
                  className="rounded-md" data-testid="book-only-notify"
                  disabled={busy || parcels.length === 0}>
            <Bell size={13} className="mr-1" /> Notify Courier
          </Button>
          <Button type="button"
                  onClick={() => submitWith({ book_pickup: true, notify_courier: true })}
                  className="rounded-md bg-primary text-primary-foreground shadow-brut-orange"
                  data-testid="book-submit" disabled={busy || parcels.length === 0}>
            <Send size={13} className="mr-1" />
            {busy ? "Booking…" : "Book & Notify"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SectionCard({ icon: Icon, title, hint, open, onToggle, testid, children }) {
  return (
    <div className="border border-border rounded-md bg-card" data-testid={testid}>
      <button type="button" onClick={onToggle}
              className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-muted/40">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {Icon && <Icon size={13} className="text-muted-foreground" />}
        <span className="overline">{title}</span>
        {hint && <span className="ml-auto text-[10px] mono text-muted-foreground">{hint}</span>}
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  );
}
